import * as turf from '@turf/turf';
import { getPolygonArea } from './geoUtils';

/**
 * 多边形 BMapGL 点简化策略
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 严格控制 BMapGL.Polygon enableParse 的开启条件
 * 2. 小面、短边面、低点数面统一关闭 parse，避免抽稀导致面退化成线
 * 3. 仅允许复杂大面开启 SDK 点简化以降低渲染压力
 *
 * 不关心业务类型，不操作 BMapGL / DOM，只做纯几何策略判断
 */

type Point = [number, number];

/** 小于等于该面积的面一律关闭 parse（平方米）：约 100m x 100m 以下都不抽稀 */
export const POLYGON_PARSE_MIN_AREA_SQM = 10000;

/** 顶点数小于等于该阈值的面一律关闭 parse：手绘业务面默认不抽稀 */
export const POLYGON_PARSE_MIN_POINT_COUNT = 200;

/** 任意相邻边小于等于该长度时一律关闭 parse（米） */
export const POLYGON_PARSE_MIN_EDGE_METERS = 2;

const isSamePoint = (a: Point, b: Point) => a[0] === b[0] && a[1] === b[1];

export const getUnclosedPolygonPoints = (points: Point[]) => {
  if (points.length > 1 && isSamePoint(points[0], points[points.length - 1])) {
    return points.slice(0, -1);
  }
  return points;
};

export const getSegmentDistanceMeters = (a: Point, b: Point) =>
  turf.distance(turf.point(a), turf.point(b), { units: 'meters' });

export const hasShortEdge = (
  points: Point[],
  minEdgeMeters = POLYGON_PARSE_MIN_EDGE_METERS
) => {
  const renderPoints = getUnclosedPolygonPoints(points);
  if (renderPoints.length < 2) {
    return true;
  }

  for (let i = 0; i < renderPoints.length; i++) {
    const current = renderPoints[i];
    const next = renderPoints[(i + 1) % renderPoints.length];
    if (getSegmentDistanceMeters(current, next) <= minEdgeMeters) {
      return true;
    }
  }

  return false;
};

/**
 * 是否允许开启 BMapGL.Polygon 的 enableParse。
 *
 * 注意：enableParse 会按当前地图级别做点抽稀。对于业务手绘面，抽掉一个近距离顶点
 * 就可能让三角形/细长面视觉退化成一条线，因此默认保守关闭；只有满足：
 * - 顶点数 > 200
 * - 面积 > 10000㎡
 * - 所有相邻边 > 2m
 * 的复杂大面才开启。
 */
export const shouldEnablePolygonParse = (points: Point[]) => {
  const renderPoints = getUnclosedPolygonPoints(points);

  if (renderPoints.length <= POLYGON_PARSE_MIN_POINT_COUNT) {
    return false;
  }

  if (getPolygonArea(renderPoints) <= POLYGON_PARSE_MIN_AREA_SQM) {
    return false;
  }

  if (hasShortEdge(renderPoints)) {
    return false;
  }

  return true;
};
